import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import RequireAuth from "@/auth/RequireAuth";
import RequireApproved from "@/auth/RequireApproved";
import AdminOnly from "@/auth/AdminOnly";
import AdminGate from "@/auth/AdminGate";
import NotFound from "./pages/NotFound";
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import AppPage from "./pages/AppPage";
import ShopifyIntegrationsRoute from "./pages/ShopifyIntegrationsRoute";
import PendingPage from "./pages/PendingPage";
import GlobalAdminPage from "./pages/GlobalAdminPage";

const queryClient = new QueryClient();

const RootRedirect = () => {
  const {
    isAuthenticated,
    loading,
    profile,
    profileLoading,
    profileError,
    isAdmin,
    adminSlug,
    adminSlugLoading,
    adminSlugError,
    getAdminSlug,
  } = useAuth();
  const [slugError, setSlugError] = useState<string | null>(null);
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(adminSlug ?? null);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated || loading || profileLoading || isAdmin || !profile?.approved) {
      return () => {
        active = false;
      };
    }

    if (!adminSlug) {
      getAdminSlug()
        .then((slug) => {
          if (!active) return;
          setResolvedSlug(slug);
          setSlugError(null);
        })
        .catch((err) => {
          if (!active) return;
          setSlugError((err as Error)?.message ?? "Erro ao obter admin_slug.");
        });
    } else if (adminSlug) {
      setResolvedSlug(adminSlug);
    }
    return () => {
      active = false;
    };
  }, [isAuthenticated, loading, profileLoading, isAdmin, profile?.approved, adminSlug, getAdminSlug]);

  if (loading || profileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (adminSlugLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando slug...</p>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-destructive">{profileError}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isAdmin) {
    return <Navigate to="/global-admin" replace />;
  }

  if (!profile || !profile.approved) {
    return <Navigate to="/pending" replace />;
  }

  if (adminSlugError || slugError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-destructive">{slugError ?? adminSlugError}</p>
      </div>
    );
  }

  if (isAuthenticated && resolvedSlug) {
    return <Navigate to={`/admin/${resolvedSlug}`} replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-sm text-muted-foreground">Preparando seu painel...</p>
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route
              path="/global-admin"
              element={
                <RequireAuth>
                  <AdminOnly>
                    <GlobalAdminPage />
                  </AdminOnly>
                </RequireAuth>
              }
            />
            <Route
              path="/pending"
              element={
                <RequireAuth>
                  <PendingPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/:slug/store"
              element={
                <RequireAuth>
                  <RequireApproved>
                    <AdminGate>
                      <AppPage initialSection="clientes" />
                    </AdminGate>
                  </RequireApproved>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/:slug/reports"
              element={
                <RequireAuth>
                  <RequireApproved>
                    <AdminGate>
                      <AppPage initialSection="relatorios" />
                    </AdminGate>
                  </RequireApproved>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/:slug/integrations/shopify"
              element={
                <RequireAuth>
                  <RequireApproved>
                    <AdminGate>
                      <ShopifyIntegrationsRoute />
                    </AdminGate>
                  </RequireApproved>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/:slug"
              element={
                <RequireAuth>
                  <RequireApproved>
                    <AdminGate>
                      <AppPage />
                    </AdminGate>
                  </RequireApproved>
                </RequireAuth>
              }
            />
            <Route path="/admin/:slug/*" element={<NotFound />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
